/**
 * Cuentas puras de «Editar categorías» (EditarCategoriasPage): qué se ve en
 * cada grupo, qué va a «Ocultas», en qué hueco cae lo que se arrastra y qué
 * pedir al servidor al soltarlo. Sin DOM, para poder probarlas.
 */
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

/** Una fila de «Ocultas»: un grupo oculto entero o una categoría oculta. */
export type FilaOculta =
  | { tipo: 'grupo'; grupo: CategoryGroupEntity }
  | {
      tipo: 'categoria';
      categoria: CategoryEntity;
      grupo: CategoryGroupEntity;
    };

/** Sitio donde caería lo arrastrado: grupo e índice entre los visibles. */
export type Hueco = { grupoId: string; indice: number };

/** Una marca en pantalla: el borde superior de un hueco posible. */
export type Marca = Hueco & { y: number };

/** Grupos que se ven arriba (sin los ocultos); Ingresos siempre al final. */
export function gruposVisibles(
  grupos: readonly CategoryGroupEntity[],
): CategoryGroupEntity[] {
  const visibles = grupos.filter(g => !g.hidden);
  return [
    ...visibles.filter(g => !g.is_income),
    ...visibles.filter(g => g.is_income),
  ];
}

/** Categorías que se ven dentro de un grupo (sin las ocultas). */
export function categoriasVisibles(
  grupo: CategoryGroupEntity,
  sin?: string | null,
): CategoryEntity[] {
  return (grupo.categories ?? []).filter(c => !c.hidden && c.id !== sin);
}

/**
 * Lo que va a «Ocultas»: los grupos ocultos (con todo lo suyo) y las
 * categorías ocultas de los grupos que se ven, en el orden del Plan.
 */
export function filasOcultas(
  grupos: readonly CategoryGroupEntity[],
): FilaOculta[] {
  const filas: FilaOculta[] = [];
  for (const grupo of grupos) {
    if (grupo.hidden) {
      filas.push({ tipo: 'grupo', grupo });
      continue;
    }
    for (const categoria of grupo.categories ?? []) {
      if (categoria.hidden) {
        filas.push({ tipo: 'categoria', categoria, grupo });
      }
    }
  }
  return filas;
}

/** El hueco cuya marca queda más cerca de `y` (null si no hay ninguno). */
export function huecoMasCercano(
  marcas: readonly Marca[],
  y: number,
): Hueco | null {
  let mejor: Marca | null = null;
  for (const m of marcas) {
    if (!mejor || Math.abs(m.y - y) < Math.abs(mejor.y - y)) {
      mejor = m;
    }
  }
  return mejor ? { grupoId: mejor.grupoId, indice: mejor.indice } : null;
}

/**
 * `category-move` coloca la categoría ANTES de `targetId` (o al final del
 * grupo con `null`). El índice cuenta solo las visibles, sin la movida.
 */
export function destinoCategoria(
  grupo: CategoryGroupEntity,
  indice: number,
  idMovida: string,
): { groupId: string; targetId: string | null } {
  const visibles = categoriasVisibles(grupo, idMovida);
  return { groupId: grupo.id, targetId: visibles[indice]?.id ?? null };
}

/**
 * `category-group-move` coloca el grupo ANTES de `targetId` (o al final con
 * `null`). El índice cuenta los grupos de gastos visibles, sin el movido;
 * al final de ellos, va delante del siguiente en el orden real (un grupo
 * oculto o Ingresos) para no saltar detrás de Ingresos.
 */
export function destinoGrupo(
  grupos: readonly CategoryGroupEntity[],
  indice: number,
  idMovido: string,
): string | null {
  const resto = grupos.filter(g => g.id !== idMovido);
  const visibles = resto.filter(g => !g.hidden && !g.is_income);
  if (indice < visibles.length) {
    return visibles[indice].id;
  }
  const ultimo = visibles[visibles.length - 1];
  const despues = ultimo ? resto.indexOf(ultimo) + 1 : 0;
  return resto[despues]?.id ?? null;
}

/** Si soltar en `hueco` deja la categoría donde estaba. */
export function mismaPosicionCategoria(
  grupos: readonly CategoryGroupEntity[],
  idMovida: string,
  hueco: Hueco,
): boolean {
  const origen = grupos.find(g => g.categories?.some(c => c.id === idMovida));
  if (!origen || origen.id !== hueco.grupoId) {
    return false;
  }
  const indice = (origen.categories ?? [])
    .filter(c => !c.hidden)
    .findIndex(c => c.id === idMovida);
  return indice === hueco.indice;
}

/** Si soltar en `indice` deja el grupo donde estaba. */
export function mismaPosicionGrupo(
  grupos: readonly CategoryGroupEntity[],
  idMovido: string,
  indice: number,
): boolean {
  return (
    grupos
      .filter(g => !g.hidden && !g.is_income)
      .findIndex(g => g.id === idMovido) === indice
  );
}

/**
 * El orden que quedará tras mover una categoría (para pintarlo al soltar,
 * sin esperar al servidor). Mismo contrato que `category-move`.
 */
export function moverCategoriaEnLista(
  grupos: readonly CategoryGroupEntity[],
  idMovida: string,
  groupId: string,
  targetId: string | null,
): CategoryGroupEntity[] {
  const movida = grupos
    .flatMap(g => g.categories ?? [])
    .find(c => c.id === idMovida);
  if (!movida) {
    return [...grupos];
  }
  return grupos.map(g => {
    const resto = (g.categories ?? []).filter(c => c.id !== idMovida);
    if (g.id !== groupId) {
      return resto.length === (g.categories ?? []).length
        ? g
        : { ...g, categories: resto };
    }
    const i = targetId ? resto.findIndex(c => c.id === targetId) : -1;
    const en = i < 0 ? resto.length : i;
    return {
      ...g,
      categories: [
        ...resto.slice(0, en),
        { ...movida, group: groupId },
        ...resto.slice(en),
      ],
    };
  });
}

/** Lo mismo para un grupo (`category-group-move`). */
export function moverGrupoEnLista(
  grupos: readonly CategoryGroupEntity[],
  idMovido: string,
  targetId: string | null,
): CategoryGroupEntity[] {
  const movido = grupos.find(g => g.id === idMovido);
  if (!movido) {
    return [...grupos];
  }
  const resto = grupos.filter(g => g.id !== idMovido);
  const i = targetId ? resto.findIndex(g => g.id === targetId) : -1;
  const en = i < 0 ? resto.length : i;
  return [...resto.slice(0, en), movido, ...resto.slice(en)];
}
